import React, { useEffect, useRef, useState } from "react";
import DOMPurify from "dompurify";
import { marked } from "marked";
import { Send, Loader2, MessageCircleQuestion, RotateCcw, Database } from "lucide-react";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { BACKEND_URL } from "../lib/backend";
import { getCsrfValue } from "../AuthContext";
import { useDarkMode } from "../hooks/useDarkMode";
import PulseLayout from "./PulseLayout";
import usePulseLocale from "./i18n";
import { tokens, fmtCompact } from "./theme";
import { pulseGet } from "./api";
import { ChartTooltipContent, SectionTitle } from "./components";

const STARTERS = [
  { q: "Which EU country has the most platform-dependent tourism?", lang: "en" },
  { q: "Compare Italy and Spain monthly nights in 2026 so far", lang: "en" },
  { q: "What does WTTC forecast for tourism's contribution to Greek GDP?", lang: "en" },
  { q: "How do OECD and UN Tourism arrival figures differ for France, and why?", lang: "en" },
  { q: "Quanto spendono i visitatori tedeschi in Toscana?", lang: "it" },
  { q: "Quali regioni italiane hanno la stagionalità più concentrata?", lang: "it" },
];

const CHART_COLORS = ["#006874", "#B45309", "#6D28D9"];

function answerHtml(md) {
  const html = marked.parse(md || "");
  const withCites = html.replace(/\[(Q|C|F)(\d{1,2})\]/g,
    '<sup class="pulse-cite">$1$2</sup>');
  return DOMPurify.sanitize(withCites);
}

const ChatChart = ({ spec, citations, dm, testid }) => {
  const { lang } = usePulseLocale();
  const T = tokens(dm);
  const periods = [...new Set(spec.series.flatMap((s) => s.points.map((p) => p[0])))].sort();
  const data = periods.map((p) => {
    const row = { period: p };
    spec.series.forEach((s) => {
      const hit = s.points.find((pt) => pt[0] === p);
      if (hit) row[s.name] = hit[1];
    });
    return row;
  });
  const ev = citations?.find((c) => c.marker === spec.evidence);
  const Comp = spec.type === "bar" ? BarChart : LineChart;
  return (
    <div className="mt-3 p-3" data-testid={testid}
      style={{ background: T.surface2, border: `1px solid ${T.border}` }}>
      {spec.title && (
        <div className="text-xs font-semibold mb-2" style={{ color: T.text }}>{spec.title}</div>
      )}
      <div style={{ height: 220 }}>
        <ResponsiveContainer width="100%" height="100%">
          <Comp data={data} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid stroke={T.border} strokeDasharray="2 4" vertical={false} />
            <XAxis dataKey="period" tick={{ fontSize: 10, fill: T.muted }} />
            <YAxis tick={{ fontSize: 10, fill: T.muted }}
              tickFormatter={(v) => fmtCompact(v, lang)} width={52} />
            <Tooltip content={<ChartTooltipContent dm={dm} formatter={(v) => fmtCompact(v, lang)} />} />
            {spec.series.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
            {spec.series.map((s, i) => spec.type === "bar" ? (
              <Bar key={s.name} dataKey={s.name} fill={CHART_COLORS[i % 3]} />
            ) : (
              <Line key={s.name} dataKey={s.name} stroke={CHART_COLORS[i % 3]}
                strokeWidth={2} dot={false} isAnimationActive={false} />
            ))}
          </Comp>
        </ResponsiveContainer>
      </div>
      {ev && (
        <span className="inline-block mt-2 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5"
          style={{ background: T.brandSoft, color: T.accent, borderRadius: 2 }}>
          {ev.display}
        </span>
      )}
    </div>
  );
};

const Citations = ({ citations, dm, idx }) => {
  const { t } = usePulseLocale();
  const T = tokens(dm);
  if (!citations?.length) return null;
  return (
    <div className="mt-3 pt-2" style={{ borderTop: `1px dashed ${T.border}` }}
      data-testid={`pulse-ask-citations-${idx}`}>
      <div className="text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: T.muted }}>
        {t("ask_sources")}
      </div>
      <div className="flex flex-col gap-1">
        {citations.map((c) => (
          <div key={c.marker} className="text-[11px] leading-relaxed" style={{ color: T.muted }}>
            <span className="font-bold px-1 mr-1.5" style={{ background: T.brandSoft, color: T.accent, borderRadius: 2 }}>
              {c.marker}
            </span>
            {c.link ? (
              <a href={c.link} target="_blank" rel="noopener noreferrer"
                data-testid={`pulse-ask-citation-link-${c.marker}`}
                className="underline underline-offset-2" style={{ color: T.text }}>
                {c.display}
              </a>
            ) : (
              <span style={{ color: T.text }}>{c.display}</span>
            )}
          </div>
        ))}
      </div>
      <details className="mt-1.5" data-testid={`pulse-ask-techref-${idx}`}>
        <summary className="text-[10px] cursor-pointer" style={{ color: T.muted }}>
          {t("tech_ref")}
        </summary>
        <div className="mt-1 flex flex-col gap-1">
          {citations.map((c) => (
            <code key={c.marker} className="text-[10px] break-all block px-1.5 py-1"
              style={{ background: T.surface2, color: T.muted, borderRadius: 2 }}>
              [{c.marker}] {c.audit}
            </code>
          ))}
        </div>
      </details>
    </div>
  );
};

export default function AskDataPage() {
  const [dm] = useDarkMode();
  const { t, lang } = usePulseLocale();
  const T = tokens(dm);
  const [question, setQuestion] = useState("");
  const [thread, setThread] = useState([]);
  const [streamText, setStreamText] = useState("");
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState(null);
  const [quota, setQuota] = useState(null);
  const [error, setError] = useState(null);
  const sessionRef = useRef(`ps_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
  const bottomRef = useRef(null);

  useEffect(() => {
    document.title = "Ask the Data — Pulse by TourismIntel";
    pulseGet("chat/quota").then(setQuota).catch(() => {});
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [thread.length, streamText]);

  const ask = async (q) => {
    const query = (q || "").trim();
    if (query.length < 5 || loading) return;
    setLoading(true); setError(null); setStreamText(""); setStage("retrieval");
    try {
      const res = await fetch(`${BACKEND_URL}/api/pulse/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": getCsrfValue() },
        body: JSON.stringify({ question: query, session_id: sessionRef.current, lang }),
      });
      if (!res.ok) {
        let detail = null;
        try { detail = (await res.json())?.detail; } catch { /* HTML page */ }
        if (res.status === 429 && detail?.reset_at) {
          setQuota({ used: detail.used, limit: detail.limit, reset_at: detail.reset_at });
          setError({ quota: true, message: detail.message });
        } else {
          setError({ message: (typeof detail === "string" && detail) || detail?.detail || t("ask_error") });
        }
        return;
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "", acc = "", finalData = null, errData = null;
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf("\n\n")) !== -1) {
          const raw = buf.slice(0, idx); buf = buf.slice(idx + 2);
          const ev = raw.match(/^event: (.+)$/m)?.[1];
          const dataStr = raw.match(/^data: (.+)$/m)?.[1];
          if (!dataStr) continue;
          let data; try { data = JSON.parse(dataStr); } catch { continue; }
          if (ev === "delta") { acc += data.text || ""; setStreamText(acc); }
          else if (ev === "status") setStage(data.stage);
          else if (ev === "final") finalData = data;
          else if (ev === "error") errData = data;
        }
      }
      if (finalData) {
        setThread((prev) => [...prev, { question: query, ...finalData }]);
        setQuestion("");
        if (finalData.quota) setQuota(finalData.quota);
      } else {
        setError({ message: errData?.message || t("ask_error") });
      }
    } catch {
      setError({ message: t("ask_error"), retry: query });
    } finally {
      setLoading(false); setStreamText(""); setStage(null);
    }
  };

  const newSession = () => {
    sessionRef.current = `ps_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    setThread([]); setError(null); setStreamText("");
  };

  const quotaReached = quota && quota.used >= quota.limit;
  const resetHm = quota?.reset_at ? `${quota.reset_at.slice(11, 16)} UTC` : "";

  return (
    <PulseLayout active="ask">
      <div className="max-w-3xl" data-testid="pulse-ask-page">
        <SectionTitle dm={dm}
          right={quota && (
            <span className="text-[11px] font-semibold px-2 py-1" data-testid="pulse-ask-quota"
              style={{ background: T.surface2, color: quotaReached ? T.down : T.muted, borderRadius: 2 }}>
              {t("ask_quota", { used: quota.used, limit: quota.limit })} · {t("ask_quota_reset", { time: resetHm })}
            </span>
          )}>
          {t("ask_title")}
        </SectionTitle>
        <p className="text-sm mb-5" style={{ color: T.muted }}>{t("ask_sub")}</p>

        {thread.length === 0 && !loading && (
          <div className="mb-6" data-testid="pulse-ask-starters">
            <div className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: T.muted }}>
              {t("ask_starters_label")}
            </div>
            <div className="flex flex-wrap gap-2">
              {STARTERS.map((s, i) => (
                <button key={i} onClick={() => ask(s.q)} data-testid={`pulse-ask-starter-${i}`}
                  className="text-xs text-left px-3 py-2 transition-colors"
                  style={{ background: T.surface, border: `1px solid ${T.border}`,
                    color: T.text, borderRadius: 2, maxWidth: 340 }}>
                  <MessageCircleQuestion className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5"
                    style={{ color: T.accent }} aria-hidden="true" />
                  {s.q}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-col gap-4">
          {thread.map((turn, i) => (
            <div key={turn.id || i} data-testid={`pulse-ask-turn-${i}`}>
              <div className="flex justify-end mb-2">
                <div className="text-sm px-3 py-2 max-w-[85%]"
                  style={{ background: T.brand, color: "#fff", borderRadius: 2 }}>
                  {turn.question}
                </div>
              </div>
              <div className="p-4" data-testid={`pulse-ask-answer-${i}`}
                style={{ background: T.surface, border: `1px solid ${T.border}` }}>
                <div className="pulse-ask-md text-sm leading-relaxed" style={{ color: T.text }}
                  dangerouslySetInnerHTML={{ __html: answerHtml(turn.answer_markdown) }} />
                {turn.chart_spec && (
                  <ChatChart spec={turn.chart_spec} citations={turn.citations} dm={dm}
                    testid={`pulse-ask-chart-${i}`} />
                )}
                <Citations citations={turn.citations} dm={dm} idx={i} />
              </div>
            </div>
          ))}

          {loading && (
            <div className="p-4" data-testid="pulse-ask-stream"
              style={{ background: T.surface, border: `1px solid ${T.border}` }}>
              {streamText ? (
                <div className="pulse-ask-md text-sm leading-relaxed" style={{ color: T.text }}
                  dangerouslySetInnerHTML={{ __html: answerHtml(streamText) }} />
              ) : (
                <div className="flex items-center gap-2 text-sm" style={{ color: T.muted }}>
                  <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                  {stage === "sql" ? t("ask_stage_sql") : t("ask_thinking")}
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="p-3 text-sm" role="alert"
              data-testid={error.quota ? "pulse-ask-quota-banner" : "pulse-ask-error"}
              style={{ background: dm ? "rgba(248,113,113,0.08)" : "#FEF2F2",
                border: `1px solid ${dm ? "#7F1D1D" : "#FECACA"}`,
                color: dm ? "#FCA5A5" : "#991B1B" }}>
              {error.message}
              {error.retry && (
                <button onClick={() => ask(error.retry)} className="ml-2 underline font-semibold"
                  data-testid="pulse-ask-retry">
                  {t("ask_retry")}
                </button>
              )}
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        <form className="mt-5 flex items-stretch gap-2"
          onSubmit={(e) => { e.preventDefault(); ask(question); }}>
          <input value={question} onChange={(e) => setQuestion(e.target.value)}
            data-testid="pulse-ask-input" maxLength={500}
            placeholder={t("ask_placeholder")}
            disabled={loading || quotaReached}
            className="flex-1 text-sm px-3 py-2.5 outline-none"
            style={{ background: T.surface, border: `1px solid ${T.border}`,
              color: T.text, borderRadius: 2 }} />
          <button type="submit" data-testid="pulse-ask-send"
            disabled={loading || quotaReached || question.trim().length < 5}
            className="px-4 flex items-center gap-1.5 text-sm font-bold disabled:opacity-40"
            style={{ background: T.brand, color: "#fff", borderRadius: 2 }}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {t("ask_send")}
          </button>
        </form>
        {thread.length > 0 && (
          <button onClick={newSession} data-testid="pulse-ask-new-session"
            className="mt-3 text-[11px] flex items-center gap-1" style={{ color: T.muted }}>
            <RotateCcw className="w-3 h-3" aria-hidden="true" /> {t("ask_new_session")}
          </button>
        )}
        <p className="mt-4 text-[10px] flex items-center gap-1" style={{ color: T.muted }}>
          <Database className="w-3 h-3" aria-hidden="true" /> {t("ask_grounding_note")}
        </p>
      </div>
    </PulseLayout>
  );
}
